import {
  Users,
  MapPin,
  Receipt,
  CreditCard,
  Megaphone,
  ClipboardList,
  FileText,
  Settings,
  ShieldCheck,
  Vote,
  LayoutDashboard,
  KeyRound,
  type LucideIcon,
} from 'lucide-react'
import type { FeatureKey } from '@riverpark/types'

export type NavRole = 'member' | 'committee' | 'estate_manager' | 'admin'

export interface NavItem {
  to: string
  icon: LucideIcon
  label: string
  /** Omitted for Dashboard and each role's own personal Bills & Payments/My Plot entries —
   * those are never gateable. Every other item maps to one of the 9 feature keys. */
  feature?: FeatureKey
}

// Profile is deliberately absent from every role's nav below — it's one tap away from the
// account-menu dropdown in the topbar (avatar/name, top right) on every page, so repeating it
// here would just be a second path to the same place.
const memberNav: NavItem[] = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/announcements', icon: Megaphone, label: 'Notices', feature: 'announcements' },
  { to: '/polls', icon: Vote, label: 'Governance', feature: 'governance' },
  { to: '/requests', icon: ClipboardList, label: 'Service Requests', feature: 'service_requests' },
  { to: '/documents', icon: FileText, label: 'Documents', feature: 'documents' },
  { to: '/bills', icon: Receipt, label: 'Bills & Payments' },
  { to: '/profile?tab=plots', icon: MapPin, label: 'My Plot' },
]

const committeeNav: NavItem[] = [
  { to: '/committee/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/committee/members', icon: Users, label: 'Members', feature: 'members' },
  { to: '/committee/plots', icon: MapPin, label: 'Plots & Map', feature: 'plots' },
  { to: '/committee/billing', icon: Receipt, label: 'Billing', feature: 'billing' },
  { to: '/committee/payments', icon: CreditCard, label: 'Payments', feature: 'payments' },
  { to: '/committee/approvals', icon: ShieldCheck, label: 'Approvals', feature: 'approvals' },
  {
    to: '/committee/announcements',
    icon: Megaphone,
    label: 'Announcements',
    feature: 'announcements',
  },
  { to: '/committee/polls', icon: Vote, label: 'Governance', feature: 'governance' },
  {
    to: '/committee/requests',
    icon: ClipboardList,
    label: 'Service requests',
    feature: 'service_requests',
  },
  { to: '/documents', icon: FileText, label: 'Documents', feature: 'documents' },
  // Committee accounts can also own a plot themselves — this is their own bills/M-Pesa checkout,
  // distinct from the org-wide "Billing" workspace above.
  { to: '/bills', icon: CreditCard, label: 'Bills & Payments' },
]

// No personal "Bills & Payments" entry here — plot-ownership.ts hard-blocks assigning a plot to
// an estate_manager account, so they can never actually owe anything on the /bills page.
const estateManagerNav: NavItem[] = [
  { to: '/operations/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/operations/members', icon: Users, label: 'Members', feature: 'members' },
  { to: '/operations/plots', icon: MapPin, label: 'Plots & Map', feature: 'plots' },
  { to: '/operations/billing', icon: Receipt, label: 'Billing', feature: 'billing' },
  { to: '/operations/payments', icon: CreditCard, label: 'Payments', feature: 'payments' },
  { to: '/operations/approvals', icon: ShieldCheck, label: 'My requests', feature: 'approvals' },
  {
    to: '/operations/announcements',
    icon: Megaphone,
    label: 'Announcements',
    feature: 'announcements',
  },
  { to: '/operations/polls', icon: Vote, label: 'Governance', feature: 'governance' },
  {
    to: '/operations/requests',
    icon: ClipboardList,
    label: 'Service requests',
    feature: 'service_requests',
  },
  { to: '/documents', icon: FileText, label: 'Documents', feature: 'documents' },
]

const adminNav: NavItem[] = [
  { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/admin/members', icon: Users, label: 'Members' },
  { to: '/admin/plots', icon: MapPin, label: 'Plots & Map' },
  { to: '/admin/billing', icon: Receipt, label: 'Billing' },
  { to: '/admin/payments', icon: CreditCard, label: 'Payments' },
  { to: '/admin/approvals', icon: ShieldCheck, label: 'Approvals' },
  { to: '/admin/announcements', icon: Megaphone, label: 'Announcements' },
  { to: '/admin/polls', icon: Vote, label: 'Governance' },
  { to: '/admin/requests', icon: ClipboardList, label: 'Service requests' },
  { to: '/admin/documents', icon: FileText, label: 'Documents' },
  { to: '/admin/access-control', icon: KeyRound, label: 'Access control' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
]

export const sidebarNavByRole: Record<NavRole, NavItem[]> = {
  member: memberNav,
  committee: committeeNav,
  estate_manager: estateManagerNav,
  admin: adminNav,
}

// Every role caps at 4 — the highest-frequency destinations only. Nothing is actually
// unreachable on mobile below this: the topbar's hamburger menu (Topbar.tsx) opens the same
// full list as the desktop sidebar (sidebarNavByRole above), so the bottom bar is a curated
// shortcut, not the only way to reach a given page.
export const bottomNavByRole: Record<NavRole, NavItem[]> = {
  // Requests dropped from here — filing one is already one tap away everywhere via the floating
  // "report an issue" button (RequestFab), and open ones already surface on the dashboard, so the
  // bottom nav's own tab was the most redundant of the four slots.
  member: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/announcements', icon: Megaphone, label: 'Notices', feature: 'announcements' },
    { to: '/bills', icon: Receipt, label: 'Bills' },
    { to: '/profile?tab=plots', icon: MapPin, label: 'My Plot' },
  ],
  // Members dropped from here — the dashboard is the new landing page and already surfaces the
  // things committee actually needs to act on; plain member lookups are estate_manager's job now.
  committee: [
    { to: '/committee/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/committee/approvals', icon: ShieldCheck, label: 'Approvals', feature: 'approvals' },
    { to: '/committee/billing', icon: Receipt, label: 'Billing', feature: 'billing' },
    { to: '/bills', icon: CreditCard, label: 'Bills' },
  ],
  // Billing dropped from here — it's a periodic task, not a many-times-daily one like member
  // lookups, plot assignment, or working through service requests.
  estate_manager: [
    { to: '/operations/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/operations/members', icon: Users, label: 'Members', feature: 'members' },
    { to: '/operations/plots', icon: MapPin, label: 'Plots', feature: 'plots' },
    {
      to: '/operations/requests',
      icon: ClipboardList,
      label: 'Requests',
      feature: 'service_requests',
    },
  ],
  // Plots dropped from here — admin's plot-write access is now a rare override rather than a
  // daily task, since estate_manager handles the registry directly.
  admin: [
    { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/admin/members', icon: Users, label: 'Members' },
    { to: '/admin/billing', icon: Receipt, label: 'Billing' },
    { to: '/admin/settings', icon: Settings, label: 'Settings' },
  ],
}
